import { ApiError, apiFetch } from '@/lib/apiFetch';
import { Fragment, useState } from 'react';
import type { AvailabilitySync, ProductionTerm, TermEshopSync } from './types';

/** Kolik dní dopředu appka drží termíny založené — musí sedět s backendem. */
const HORIZON_DAYS = 21;

const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰' };

/** 'YYYY-MM-DD' → 'DD.MM.YYYY'. */
function fmtCzDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

type Bucket = { done: number; total: number };
type Buckets = { cz_avail: Bucket; cz_status: Bucket; sk_avail: Bucket; sk_status: Bucket };

function emptyBuckets(): Buckets {
  return {
    cz_avail: { done: 0, total: 0 },
    cz_status: { done: 0, total: 0 },
    sk_avail: { done: 0, total: 0 },
    sk_status: { done: 0, total: 0 },
  };
}

const OK_STATUSES = new Set(['created', 'exists']);

function computeBuckets(terms: ProductionTerm[]): Buckets {
  const b = emptyBuckets();
  for (const t of terms) {
    for (const e of t.eshops) {
      const availKey = e.country === 'SK' ? 'sk_avail' : 'cz_avail';
      const statusKey = e.country === 'SK' ? 'sk_status' : 'cz_status';
      b[availKey].total++;
      if (e.availability && OK_STATUSES.has(e.availability.status)) b[availKey].done++;
      b[statusKey].total++;
      if (e.order_status && OK_STATUSES.has(e.order_status.status)) b[statusKey].done++;
    }
  }
  return b;
}

function ProgressBar({ label, b }: { label: string; b: Bucket }) {
  const pct = b.total > 0 ? Math.round((b.done / b.total) * 100) : 100;
  const complete = b.total > 0 && b.done === b.total;
  return (
    <div>
      <div className="flex items-center justify-between text-xs font-semibold text-text-secondary mb-1">
        <span>{label}</span>
        <span className={complete ? 'text-success' : 'text-text'}>
          {b.done}/{b.total} {b.total > 0 && `(${pct} %)`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-bg overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${complete ? 'bg-success' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatusBadge({
  s,
}: {
  s: {
    status: 'created' | 'exists' | 'missing' | 'failed';
    remote_name?: string;
    error_message: string | null;
  } | null;
}) {
  if (!s) return <span className="text-text-muted">· nezjištěno</span>;
  if (s.status === 'created' || s.status === 'exists') {
    return <span className="text-success font-semibold">✓ založeno</span>;
  }
  if (s.status === 'missing') {
    return (
      <span className="text-warning font-semibold">
        ⚠ chybí{s.remote_name ? ` — založ ručně: ${s.remote_name}` : ''}
      </span>
    );
  }
  return (
    <span className="text-danger font-semibold" title={s.error_message ?? ''}>
      ✕ chyba{s.error_message ? `: ${s.error_message}` : ''}
    </span>
  );
}

function isComplete(t: ProductionTerm): boolean {
  if (t.is_service) return true;
  if (t.eshops.length === 0) return false;
  return t.eshops.every(
    (e) =>
      e.availability &&
      OK_STATUSES.has(e.availability.status) &&
      e.order_status &&
      OK_STATUSES.has(e.order_status.status),
  );
}

/** Poslední SOUVISLÉ datum od prvního nadcházejícího termínu, kde je `ok` pravda — zastaví se na první mezeře. */
function readyThrough(terms: ProductionTerm[], ok: (t: ProductionTerm) => boolean): string | null {
  let last: string | null = null;
  for (const t of terms) {
    if (!ok(t)) break;
    last = t.term_date;
  }
  return last;
}

/** 'YYYY-MM-DD' → 'D. M.' (krátce, pro hlavičku mřížky). */
function fmtShort(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number(d)}.${Number(m)}.` : iso;
}

const CELL_COLOR: Record<string, string> = {
  ok: 'bg-success',
  missing: 'bg-warning',
  failed: 'bg-danger',
  unknown: 'bg-border-light',
};

/** Jedna buňka mřížky — barva podle stavu, datum + stav v title (tooltip). */
function GridCell({
  s,
  termDate,
}: {
  s: { status: 'created' | 'exists' | 'missing' | 'failed' } | null;
  termDate: string;
}) {
  const key = !s
    ? 'unknown'
    : OK_STATUSES.has(s.status)
      ? 'ok'
      : s.status === 'missing'
        ? 'missing'
        : 'failed';
  const label = !s
    ? 'nezjištěno'
    : OK_STATUSES.has(s.status)
      ? 'založeno'
      : s.status === 'missing'
        ? 'chybí'
        : 'chyba';
  return (
    <div
      className={`w-[26px] h-[18px] rounded ${CELL_COLOR[key]}`}
      title={`${fmtCzDate(termDate)} — ${label}`}
    />
  );
}

/** Kalendářová mřížka: 4 řádky (CZ/SK × dostupnost/stav) × sloupec na termín. */
function ReadinessGrid({ terms }: { terms: ProductionTerm[] }) {
  const rows: {
    label: string;
    get: (t: ProductionTerm) => { status: 'created' | 'exists' | 'missing' | 'failed' } | null;
  }[] = [
    {
      label: '🇨🇿 Dostupnost',
      get: (t) => t.eshops.find((e) => e.country !== 'SK')?.availability ?? null,
    },
    {
      label: '🇨🇿 Stav obj.',
      get: (t) => t.eshops.find((e) => e.country !== 'SK')?.order_status ?? null,
    },
    {
      label: '🇸🇰 Dostupnost',
      get: (t) => t.eshops.find((e) => e.country === 'SK')?.availability ?? null,
    },
    {
      label: '🇸🇰 Stav obj.',
      get: (t) => t.eshops.find((e) => e.country === 'SK')?.order_status ?? null,
    },
  ];

  return (
    <div className="overflow-x-auto">
      <div
        className="inline-grid gap-1 items-center"
        style={{ gridTemplateColumns: `110px repeat(${terms.length}, 26px)` }}
      >
        <div />
        {terms.map((t) => (
          <div
            key={t.id}
            className="text-[9px] text-text-muted text-center whitespace-nowrap"
            title={fmtCzDate(t.term_date)}
          >
            {fmtShort(t.term_date)}
          </div>
        ))}
        {rows.map((r) => (
          <Fragment key={r.label}>
            <div className="text-xs font-medium text-text-secondary">{r.label}</div>
            {terms.map((t) => (
              <GridCell key={t.id} s={r.get(t)} termDate={t.term_date} />
            ))}
          </Fragment>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-text-muted">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded bg-success inline-block" /> založeno
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded bg-warning inline-block" /> chybí
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded bg-danger inline-block" /> chyba
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded bg-border-light inline-block" /> nezjištěno
        </span>
      </div>
    </div>
  );
}

/**
 * Přehled dokončenosti termínů výroby na nejbližší ~3 týdny — kolik z nich
 * má hotovou dostupnost a stav objednávky v Shoptetu (CZ i SK), s progress
 * bary. Tlačítko "Doplnit termíny" založí chybějící termíny + dostupnosti
 * (jede i samo každou noc jako druhá půlka cronu po úklidu propadlých
 * dostupností). Stav objednávky Shoptet API založit neumí — chybějící se tu
 * jen ukáže a pošle na Telegram, založit se musí ručně v administraci.
 */
export function TerminyPrehled({
  terms,
  onRefresh,
}: {
  terms: ProductionTerm[] | null;
  onRefresh: () => Promise<void>;
}) {
  const [running, setRunning] = useState(false);
  const [recheckingId, setRecheckingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (terms === null) return <p className="text-sm text-text-muted">Načítám…</p>;

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Prague' });
  const horizon = new Date(Date.now() + HORIZON_DAYS * 86_400_000).toLocaleDateString('en-CA', {
    timeZone: 'Europe/Prague',
  });
  const upcoming = terms
    .filter((t) => !t.is_service && t.term_date >= today && t.term_date <= horizon)
    .sort((a, b) => a.term_date.localeCompare(b.term_date));
  const buckets = computeBuckets(upcoming);
  const incomplete = upcoming.filter((t) => !isComplete(t));
  const readyAll = readyThrough(upcoming, isComplete);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      await apiFetch('/api/vyroba/terms/ensure-upcoming', { method: 'POST' });
      await onRefresh();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Doplnění selhalo');
    } finally {
      setRunning(false);
    }
  }

  async function recheck(id: number) {
    setRecheckingId(id);
    setError(null);
    try {
      await apiFetch<{ sync: AvailabilitySync[] }>(`/api/vyroba/terms/${id}/sync-availability`, {
        method: 'POST',
      });
      await onRefresh();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Kontrola selhala');
    } finally {
      setRecheckingId(null);
    }
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <p className="text-sm text-text-secondary max-w-3xl">
          Drží termíny výroby vždycky založené na nejbližší <strong>3 týdny</strong> dopředu — u
          každého rovnou zkusí založit i dostupnost v Shoptetu (CZ i SK). Stav objednávky („Došíváme
          - odeslat…") Shoptet API založit neumí — chybějící se ukáže níže a pošle na Telegram,
          založit se musí ručně v administraci. Běží i automaticky každou noc.
        </p>
        <button
          type="button"
          onClick={run}
          disabled={running}
          className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg px-5 py-2.5 cursor-pointer transition-colors shrink-0"
        >
          {running ? 'Probíhá…' : 'Doplnit termíny'}
        </button>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <ProgressBar label="🇨🇿 Dostupnost" b={buckets.cz_avail} />
        <ProgressBar label="🇸🇰 Dostupnost" b={buckets.sk_avail} />
        <ProgressBar label="🇨🇿 Stav objednávky" b={buckets.cz_status} />
        <ProgressBar label="🇸🇰 Stav objednávky" b={buckets.sk_status} />
      </div>

      <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-5">
        <div className="text-sm mb-3">
          <span className="text-text-secondary">
            Kompletně (CZ i SK, dostupnost i stav) hotovo do:{' '}
          </span>
          {readyAll ? (
            <span className="font-bold text-success">{fmtCzDate(readyAll)}</span>
          ) : (
            <span className="font-bold text-warning">zatím žádný nadcházející termín</span>
          )}
        </div>
        <ReadinessGrid terms={upcoming} />
      </div>

      {incomplete.length === 0 ? (
        <div className="bg-surface rounded-xl shadow-card px-5 py-4">
          <p className="text-sm text-success font-semibold">
            ✓ Všechny termíny na nejbližší 3 týdny mají vše založené.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {incomplete.map((t) => (
            <div key={t.id} className="bg-surface rounded-xl shadow-card px-5 py-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-sm font-bold text-text">Termín {fmtCzDate(t.term_date)}</div>
                <button
                  type="button"
                  onClick={() => recheck(t.id)}
                  disabled={recheckingId === t.id}
                  className="text-[13px] font-semibold text-primary hover:underline cursor-pointer disabled:opacity-50 shrink-0"
                >
                  {recheckingId === t.id ? 'Kontroluji…' : 'Zkontrolovat znovu'}
                </button>
              </div>
              {t.eshops.length === 0 ? (
                <p className="text-sm text-text-muted">Zatím nepropojeno s e-shopem.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {t.eshops.map((e: TermEshopSync) => (
                    <div key={e.eshop_code} className="text-sm text-text-secondary space-y-0.5">
                      <div className="font-medium text-text">
                        <span aria-hidden>{COUNTRY_FLAG[e.country] ?? '🏳️'}</span> {e.eshop_name}
                      </div>
                      <div className="pl-5">
                        Dostupnost: <StatusBadge s={e.availability} />
                      </div>
                      <div className="pl-5">
                        Stav objednávky: <StatusBadge s={e.order_status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
